import { t } from "@lingui/core/macro";
import { api } from "@/lib/api-client";
import { Trash2 } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import {
  addToast,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react";
import { AlertButton } from "../NextBase/AlertButton";
import { useCallback } from "react";

type TableItem = {
  id: number;
  scheduledTime: string;
  channel: string;
};

export const UserAppointmentMessageList = ({
  userAppointmentId,
}: {
  userAppointmentId: number;
}) => {
  const { data, isLoading, isSuccess, refetch } =
    api.message.useGetUserAppointmentMessageSchedule({
      userAppointmentId,
    });

  const { mutateAsync: deleteMessageSchedule } =
    api.message.useDeleteMessageScheduleById();

  const handleDeleteMessageSchedule = async (id: number) => {
    await deleteMessageSchedule(id)
      .then(async () => {
        addToast({
          title: t`Sporočilo je bilo uspešno izbrisano`,
          color: "success",
        });
        await refetch();
      })
      .catch((e) => {
        addToast({
          title: t`Napaka pri brisanju sporočila`,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          description: (e as any).message,
          color: "danger",
        });
      });
  };

  const renderCell = useCallback(
    (item: TableItem, columnKey: React.Key) => {
      const cellValue = item[columnKey as keyof TableItem];

      switch (columnKey) {
        case "actions":
          return (
            <div className="relative flex items-center gap-2">
              <AlertButton
                title={t`Izbriši sporočilo`}
                content={t`Ali ste prepričani, da želite izbrisati sporočilo?`}
                buttonProps={{
                  isIconOnly: true,
                  variant: "flat",
                  color: "danger",
                  size: "sm",
                }}
                confirmProps={{
                  onClick: () => handleDeleteMessageSchedule(item.id),
                }}
              >
                <Trash2 size={16} />
              </AlertButton>
            </div>
          );
        default:
          return cellValue;
      }
    },
    [handleDeleteMessageSchedule],
  );

  const columns = [
    {
      key: "scheduledTime",
      label: t`Čas`,
    },
    {
      key: "channel",
      label: t`Kanal`,
    },
    {
      key: "actions",
      label: t`Akcije`,
    },
  ];

  const rows =
    data?.userAppointmentMessageSchedule.map((message) => ({
      id: message.id,
      scheduledTime: message.scheduledTime,
      channel: message.channel,
    })) ?? [];

  if (isLoading || !isSuccess) return null;

  return (
    <>
      {data.userAppointmentMessageSchedule.length === 0 && (
        <span className="text-sm text-gray-500">
          <Trans>Ni sporočil</Trans>
        </span>
      )}

      <Table aria-label={t`Tabela sporočil`} removeWrapper isCompact>
        <TableHeader columns={columns}>
          {(column) => (
            <TableColumn key={column.key}>{column.label}</TableColumn>
          )}
        </TableHeader>
        <TableBody items={rows}>
          {(item) => (
            <TableRow key={item.id}>
              {(columnKey) => (
                <TableCell>{renderCell(item, columnKey)}</TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};
